import { type ColumnDef } from "@tanstack/react-table";
import {
  PlusIcon,
  UserIcon as UserPlaceholderIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { DataTable } from "@/components/data-table";
import { Button } from "@/components/ui/button";
import { PageContainer, PageHeader } from "@/components/layout";
import AddUserDialog from "../../components/users/add-user-dialog";
import { ConfirmDialog } from "../../components/confirm-dialog";
import { MediaViewer, photosToMedia } from "../../components/media-gallery";
import EditUserDialog from "../../components/users/edit-user-dialog";
import { UserIcon } from "../../components/UserIcon";
import { useNotificationsProvider } from "../../context/NotificationsContext";
import { selectUser } from "../login/selectors";
import { useDeleteUserMutation, useUserListQuery } from "../../services/api";

interface UserRow {
  id: string;
  fullName: string;
  roleName: string;
  photoUrl?: string | null;
}

interface UsersResponse {
  items?: UserRow[];
  itemCount?: number;
  range?: { limit?: number; page?: number };
}

interface DeleteValidationError {
  errorCode?: string;
}

interface ApiErrorPayload {
  response?: {
    data?: { validationErrors?: DeleteValidationError[] };
  };
}

export default function Users() {
  const { t } = useTranslation();
  const { showNotification } = useNotificationsProvider();

  const [page, setPage] = useState(0);
  // `{}` cast: EditUserDialog's resetState reads `value.X` directly and the
  // dialog renders unconditionally with the prop; passing `null` initially
  // crashes the dialog on first mount.
  const [selectedUser, setSelectedUser] = useState<UserRow>({} as UserRow);
  const [openAddUserDialog, setOpenAddUserDialog] = useState(false);
  const [openEditUserDialog, setOpenEditUserDialog] = useState(false);
  const [editOnOpen, setEditOnOpen] = useState(false);
  const [openDeleteUserDialog, setOpenDeleteUserDialog] = useState(false);
  const [openGallery, setOpenGallery] = useState(false);
  const [galleryImages, setGalleryImages] = useState<string[]>([]);

  const { data: users, isFetching: fetchingList } = useUserListQuery({
    range: { page: page + 1 },
  }) as { data?: UsersResponse; isFetching: boolean };
  const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation();
  const loggedUser = useSelector(selectUser()) as { id?: string } | null;

  function onView(row: UserRow) {
    setSelectedUser(row);
    setEditOnOpen(false);
    setOpenEditUserDialog(true);
  }

  async function onDeleted() {
    if (!selectedUser?.id) return;
    try {
      await deleteUser(selectedUser.id).unwrap();
      showNotification(
        "success",
        t("user.delete-user-dialog.success", { name: selectedUser.fullName }),
      );
    } catch (err) {
      const errorCode = (err as ApiErrorPayload)?.response?.data
        ?.validationErrors?.[0]?.errorCode;
      if (errorCode === "CANNOT_DELETE_SELF") {
        showNotification("error", t("user.delete-user-dialog.error-own-user"));
      } else if (errorCode === "CANNOT_DELETE_LAST_ADMIN") {
        showNotification(
          "error",
          t("user.delete-user-dialog.error-last-admin"),
        );
      } else {
        showNotification(
          "error",
          t("user.delete-user-dialog.error", { name: selectedUser.fullName }),
        );
      }
      // Re-throw so ConfirmDialog keeps itself open for retry.
      throw new Error("delete-failed");
    }
  }

  const isCurrentUser = (row: UserRow) => row.id === loggedUser?.id;

  // No per-row Edit / Delete columns — row click opens the user sheet
  // in view mode; the sheet header carries the read↔edit toggle and the
  // destructive trash icon. `isCurrentUser` is still consulted before
  // wiring onDelete on the sheet so users can never delete themselves
  // from the row OR the sheet.
  const columns = useMemo<ColumnDef<UserRow>[]>(
    () => [
      {
        id: "photoUrl",
        header: " ",
        enableSorting: false,
        cell: ({ row }) =>
          row.original.photoUrl ? (
            <UserIcon
              src={row.original.photoUrl}
              width={32}
              height={32}
              onClick={(e) => {
                e?.stopPropagation?.();
                setGalleryImages([row.original.photoUrl as string]);
                setOpenGallery(true);
              }}
            />
          ) : (
            <UserPlaceholderIcon className="size-7 text-muted-foreground" />
          ),
      },
      {
        accessorKey: "fullName",
        header: t("user.items.name"),
      },
      {
        accessorKey: "roleName",
        header: t("user.items.role"),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t]
  );

  const rows = users?.items ?? [];
  const pageSize = users?.range?.limit ?? 15;
  const pageCount = users?.itemCount
    ? Math.max(1, Math.ceil(users.itemCount / pageSize))
    : 1;

  return (
    <>
      <ConfirmDialog
        open={openDeleteUserDialog}
        onOpenChange={(next) => { if (!next) setOpenDeleteUserDialog(false); }}
        title={t("user.delete-user-dialog.title", {
          name: selectedUser.fullName,
        })}
        confirmLabel={t("common.delete")}
        onConfirm={onDeleted}
        isPending={isDeleting}
      />
      <AddUserDialog
        title={t("user.add-user-dialog.title")}
        open={openAddUserDialog}
        onClose={() => setOpenAddUserDialog(false)}
        onCreated={() => setOpenAddUserDialog(false)}
      />
      <EditUserDialog
        title={t("user.edit-user-dialog.title")}
        value={selectedUser}
        open={openEditUserDialog}
        initialEditing={editOnOpen}
        onClose={() => setOpenEditUserDialog(false)}
        onEdited={() => setOpenEditUserDialog(false)}
        onDelete={
          selectedUser?.id && !isCurrentUser(selectedUser)
            ? () => setOpenDeleteUserDialog(true)
            : undefined
        }
      />
      <MediaViewer
        open={openGallery}
        onOpenChange={setOpenGallery}
        items={photosToMedia(galleryImages)}
        initialIndex={0}
      />
      <PageContainer title={t("user.tab-title")}>
        <PageHeader
          title={t("user.page-title")}
          description={t("user.info-message")}
          actions={
            <Button onClick={() => setOpenAddUserDialog(true)}>
              <PlusIcon className="size-4" />
              {t("user.add-user")}
            </Button>
          }
        />
        <div className="mt-6">
          <DataTable
            columns={columns}
            data={rows}
            emptyMessage={t("common.no-data", "No data")}
            onRowClick={onView}
            loading={fetchingList}
            skeletonRows={pageSize}
            manualPagination={{
              pageIndex: page,
              pageCount,
              onPageChange: setPage,
            }}
          />
        </div>
      </PageContainer>
    </>
  );
}
